<script setup lang="ts">
import type { ComponentBlockPriceCardFragment } from "~~/types/graphql";

import { Enum_Componentblockpricecard_Variation } from "~~/types/graphql";

const props = defineProps<{
  PriceCardTag: ComponentBlockPriceCardFragment["PriceCardTag"];
  PriceCardHeadline: ComponentBlockPriceCardFragment["PriceCardHeadline"];
  PriceCardDescription: ComponentBlockPriceCardFragment["PriceCardDescription"];
  PriceCardItems: ComponentBlockPriceCardFragment["PriceCardItems"];
  PriceCardVariation: Enum_Componentblockpricecard_Variation;
}>();

const gridCols = computed(() => {
  if (props.PriceCardItems == null) return 1;
  if (props.PriceCardItems.length >= 4) {
    return 4;
  } else {
    return props.PriceCardItems.length;
  }
});

const { t } = useI18n({
  useScope: "local",
});
</script>

<i18n lang="json">
{
  "en": {
    "startNow": "Get started now",
    "vat": "excl. VAT"
  },
  "de": {
    "startNow": "Jetzt durchstarten",
    "vat": "exkl. MwSt."
  }
}
</i18n>

<template>
  <layout-block
    class="price-card"
    :class="{
      'bg-integer-black text-integer-white':
        PriceCardVariation === Enum_Componentblockpricecard_Variation.Dark,
    }"
  >
    <div class="flex flex-col items-center">
      <base-tag v-if="PriceCardTag">{{ PriceCardTag }}</base-tag>
      <h2
        class="hyphens-auto text-center text-h2"
        :class="{ 'mt-5.2': PriceCardTag }"
      >
        {{ PriceCardHeadline }}
      </h2>
      <p class="mt-5.6 max-w-prose text-center text-default">
        {{ PriceCardDescription }}
      </p>
    </div>
    <div
      :class="`lg:grid-cols-${gridCols} grid sm:grid-cols-2`"
      class="mt-5.4 grid gap-5.4 py-5.4"
    >
      <base-card
        v-for="(item, index) in PriceCardItems"
        :key="`${item?.__typename}-${index}`"
        :variation="PriceCardVariation"
        class="mx-auto flex w-full flex-col justify-between transition"
        :class="{
          'border border-integer-black !bg-white':
            PriceCardVariation === 'light' && item?.highlight,
          'border border-light-black !bg-black':
            PriceCardVariation === 'dark' && item?.highlight,
          'max-w-md': PriceCardItems?.length === 1,
        }"
      >
        <div>
          <div class="flex justify-between">
            <base-tag
              v-if="item?.tag"
              class="h-fit"
              :type="PriceCardVariation === 'dark' ? 'tertiary' : 'primary'"
            >
              {{ item?.tag }}
            </base-tag>
            <base-image
              v-if="item?.icon?.data?.attributes"
              class="h-[2.75rem] w-[2.75rem]"
              v-bind="item.icon.data.attributes"
              sizes="2.75rem"
            />
          </div>
          <div
            :class="{
              'mt-5.5': item?.tag || item?.icon?.data?.attributes,
              'mt-[4.25rem]': !item?.tag && !item?.icon?.data?.attributes,
            }"
          >
            <h3 class="text-h3">{{ item?.name }}</h3>
            <p class="mt-[0.75rem] text-xs font-normal">
              {{ item?.description }}
            </p>
          </div>
          <div class="mt-5.5 flex flex-col text-default">
            <div v-if="item?.price">
              <span v-if="item?.oldPrice" class="line-through">{{
                new Intl.NumberFormat("de-DE", {
                  style: "currency",
                  currency: "EUR",
                  maximumFractionDigits: 0,
                }).format(Number(item.oldPrice))
              }}</span>
              <div
                :class="{ 'mt-[3rem]': !item?.oldPrice }"
                class="font-pixel text-h2"
              >
                {{
                  new Intl.NumberFormat("de-DE", {
                    style: "currency",
                    currency: "EUR",
                    maximumFractionDigits: 0,
                  }).format(Number(item?.price))
                }}
                <span class="text-2xs font-normal">{{ t("vat") }}</span>
              </div>
            </div>
          </div>
          <div v-if="item?.benefits" class="mt-5.8">
            <base-text :Text="item.benefits" />
          </div>
        </div>

        <div
          v-if="PriceCardItems?.some((itm) => itm?.link)"
          class="mt-5.11 flex items-center justify-center"
        >
          <base-button
            v-if="item?.link"
            :type="item?.highlight ? 'primary' : 'secondary'"
            class="w-full justify-center"
            :class="{
              'border-integer-white hover:bg-black':
                PriceCardVariation === 'dark' && item?.highlight,
              'border-integer-white text-integer-white hover:bg-integer-white hover:text-integer-black':
                PriceCardVariation === 'dark' && !item?.highlight,
            }"
            :transparent="item?.highlight ? false : true"
            :to="item?.link"
            >{{ item?.buttonText ?? t("startNow") }}</base-button
          >
        </div>
      </base-card>
    </div>
  </layout-block>
</template>

<style scoped>
:deep(li) {
  list-style-image: url("../../assets/icons/check.svg");
  @apply text-xs;
}
</style>
